import React, { useState, useLayoutEffect } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Checkbox } from '../ui/checkbox';
import { Separator } from '../ui/separator';
import { Textarea } from '../ui/textarea';
import { Plus, Trash2, ArrowLeft, ArrowRight, Loader2, Upload, FileText } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const ApplicationForm = ({ onBack, axiosInstance, readOnly = false, initialData = null }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [step, setStep] = useState(1); // 1: Personal/Family/Contact, 2: Educational

  useLayoutEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [step]); // Scroll to top when step changes
  
  const { register, control, handleSubmit, trigger, setValue, formState: { errors }, getValues } = useForm({
    shouldUnregister: false,
    defaultValues: initialData || {
      position_applied: '',
      application_date: new Date().toISOString().split('T')[0],
      personal_info: {
        last_name: '',
        first_name: '',
        middle_name: '',
        nickname: '',
        current_address: '',
        permanent_address: '',
        provincial_address: '',
        dob: '',
        age: '',
        civil_status: '',
        date_of_marriage: '',
      },
      family_background: {
        spouse: { name: '', age: '', occupation: '', contact_number: '' },
        children: [],
        mother: { name: '', dob: '', age: '', occupation: '', is_deceased: false },
        father: { name: '', dob: '', age: '', occupation: '', is_deceased: false },
      },
      contact_info: {
        mobile_number: '',
        landline_number: '',
        email_address: '',
        skype_yahoo: '',
      },
      government_ids: {
        sss_no: '',
        tin_no: '',
        philhealth_no: '',
        pagibig_no: '',
        unified_id_no: '',
        passport_no: '',
        drivers_license_no: '',
        others: '',
      },
      emergency_contact: {
        name: '',
        relationship: '',
        contact_number: '',
      },
      educational_background: {
        post_graduate: { school: '', course: '', from_date: '', to_date: '' },
        college: { school: '', course: '', from_date: '', to_date: '' },
        secondary: { school: '', address: '', from_date: '', to_date: '' },
        primary: { school: '', address: '', from_date: '', to_date: '' },
        honors_awards: '',
        extra_curricular: '',
        government_exams: '',
        seminars_trainings: []
      },
      employment_history: [],
      referral_info: {
        source: '',
        has_relatives: false,
        relatives: []
      },
      medical_background: {
        is_pregnant: false,
        delivery_date: '',
        conditions: [],
        conditions_explanation: '',
        medications: '',
        hospitalization_history: '',
        advised_treatment: '',
        surgical_procedure: '',
        physician_visit: ''
      },
      character_references: [],
      declaration: {
        signature_name: '',
        date: new Date().toISOString().split('T')[0]
      },
      resume_file: ''
    }
  });

  const { fields: childFields, append: appendChild, remove: removeChild } = useFieldArray({
    control,
    name: "family_background.children"
  });

  const { fields: seminarFields, append: appendSeminar, remove: removeSeminar } = useFieldArray({
    control,
    name: "educational_background.seminars_trainings"
  });

  const { fields: employmentFields, append: appendEmployment, remove: removeEmployment } = useFieldArray({
    control,
    name: "employment_history"
  });
  
  const { fields: relativeFields, append: appendRelative, remove: removeRelative } = useFieldArray({
    control,
    name: "referral_info.relatives"
  });

  const { fields: referenceFields, append: appendReference, remove: removeReference } = useFieldArray({
    control,
    name: "character_references"
  });

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setIsUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const api = axiosInstance || axios.create({
        baseURL: process.env.REACT_APP_BACKEND_URL + '/api'
      });
      
      const response = await api.post('/hris/applications/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setValue('resume_file', response.data.url);
      toast.success("File uploaded successfully");
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("File upload failed");
    } finally {
      setIsUploading(false);
    }
  };

  const handleNext = async () => {
    if (step === 1) {
      // Validate step 1 fields
      const isValid = await trigger([
        "position_applied",
        "personal_info",
        "contact_info",
        "emergency_contact"
      ]);
      
      if (isValid) {
        setStep(2);
      } else {
        toast.error("Please fill in all required fields correctly.");
      }
    } else if (step === 2) {
      // Validate step 2 if needed (currently most fields are optional in step 2)
      setStep(3);
    } else if (step === 3) {
      setStep(4);
    }
  };

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      const api = axiosInstance || axios.create({
        baseURL: process.env.REACT_APP_BACKEND_URL + '/api'
      });

      await api.post('/hris/applications', data);
      
      toast.success("Application submitted successfully!");
      setTimeout(() => {
        onBack();
      }, 2000);
      
    } catch (error) {
      console.error("Submission error:", error);
      toast.error(error.response?.data?.detail || "Failed to submit application. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8 animate-in fade-in slide-in-from-bottom-8 duration-700">
      <div className="max-w-4xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <Button 
            variant="ghost" 
            onClick={step === 1 ? onBack : () => setStep(1)} 
            className="hover:bg-gray-200"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            {step === 1 ? "Back to Candidates" : "Back to Step 1"}
          </Button>
          <div className="text-sm font-medium text-gray-500">
            Step {step} of 4
          </div>
        </div>

        <Card className="shadow-xl border-t-4 border-t-blue-600">
          <CardHeader className="text-center bg-white border-b">
            <CardTitle className="text-2xl font-bold text-gray-900">
              {step === 1 ? "EMPLOYMENT APPLICATION FORM" : step === 2 ? "EDUCATIONAL & EMPLOYMENT" : step === 3 ? "MEDICAL BACKGROUND" : "REFERENCES & DECLARATION"}
              {readOnly && <span className="ml-2 text-sm font-normal text-gray-500">(Read-Only Mode)</span>}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 sm:p-8 bg-white">
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
              
              {/* STEP 1: Personal, Family, Contact, IDs */}
              {step === 1 && (
                <div className="animate-in fade-in slide-in-from-right-8 duration-500">
                  {/* Top Section */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="position_applied">Position Applied <span className="text-red-500">*</span></Label>
                      <Input 
                        id="position_applied" 
                        disabled={readOnly}
                        {...register("position_applied", { required: "Position is required" })} 
                        className={errors.position_applied ? "border-red-500" : ""}
                      />
                      {errors.position_applied && <span className="text-xs text-red-500">{errors.position_applied.message}</span>}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="application_date">Application Date</Label>
                      <Input 
                        id="application_date" 
                        type="date"
                        disabled={readOnly}
                        {...register("application_date")} 
                      />
                    </div>
                  </div>

                  <Separator className="my-6" />

                  {/* Personal Information */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900">PERSONAL INFORMATION</h3>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div className="space-y-2">
                        <Label>Last Name <span className="text-red-500">*</span></Label>
                        <Input disabled={readOnly} {...register("personal_info.last_name", { required: true })} />
                      </div>
                      <div className="space-y-2">
                        <Label>First Name <span className="text-red-500">*</span></Label>
                        <Input disabled={readOnly} {...register("personal_info.first_name", { required: true })} />
                      </div>
                      <div className="space-y-2">
                        <Label>Middle Name</Label>
                        <Input disabled={readOnly} {...register("personal_info.middle_name")} />
                      </div>
                      <div className="space-y-2">
                        <Label>Nickname</Label>
                        <Input disabled={readOnly} {...register("personal_info.nickname")} />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Current Address (Rented/Owned) <span className="text-red-500">*</span></Label>
                      <Input disabled={readOnly} {...register("personal_info.current_address", { required: true })} />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Permanent Address (Rented/Owned)</Label>
                        <Input disabled={readOnly} {...register("personal_info.permanent_address")} />
                      </div>
                      <div className="space-y-2">
                        <Label>Provincial Address (Rented/Owned)</Label>
                        <Input disabled={readOnly} {...register("personal_info.provincial_address")} />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div className="space-y-2">
                        <Label>Date of Birth <span className="text-red-500">*</span></Label>
                        <Input type="date" disabled={readOnly} {...register("personal_info.dob", { required: true })} />
                      </div>
                      <div className="space-y-2">
                        <Label>Age</Label>
                        <Input type="number" disabled={readOnly} {...register("personal_info.age")} />
                      </div>
                      <div className="space-y-2">
                        <Label>Civil Status</Label>
                        <Input disabled={readOnly} {...register("personal_info.civil_status")} />
                      </div>
                      <div className="space-y-2">
                        <Label>If Married (Date of Marriage)</Label>
                        <Input type="date" disabled={readOnly} {...register("personal_info.date_of_marriage")} />
                      </div>
                    </div>
                  </div>

                  <Separator className="my-6" />

                  {/* Family Background */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900">FAMILY BACKGROUND</h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 p-4 bg-gray-50 rounded-lg">
                      <div className="md:col-span-1 space-y-2">
                        <Label>Spouse's Name</Label>
                        <Input disabled={readOnly} {...register("family_background.spouse.name")} />
                      </div>
                      <div className="md:col-span-1 space-y-2">
                        <Label>Age</Label>
                        <Input type="number" disabled={readOnly} {...register("family_background.spouse.age")} />
                      </div>
                      <div className="md:col-span-1 space-y-2">
                        <Label>Occupation</Label>
                        <Input disabled={readOnly} {...register("family_background.spouse.occupation")} />
                      </div>
                      <div className="md:col-span-1 space-y-2">
                        <Label>Contact Number</Label>
                        <Input disabled={readOnly} {...register("family_background.spouse.contact_number")} />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <Label className="font-medium">Children</Label>
                        {!readOnly && (
                          <Button 
                            type="button" 
                            variant="outline" 
                            size="sm" 
                            onClick={() => appendChild({ name: '', dob: '', age: '' })}
                            className="text-blue-600 border-blue-200 hover:bg-blue-50"
                          >
                            <Plus className="h-4 w-4 mr-1" /> Add Child
                          </Button>
                        )}
                      </div>
                      
                      {childFields.map((field, index) => (
                        <div key={field.id} className="grid grid-cols-1 md:grid-cols-7 gap-4 items-end bg-gray-50 p-3 rounded-lg">
                          <div className="md:col-span-3 space-y-2">
                            <Label>Child's Name</Label>
                            <Input disabled={readOnly} {...register(`family_background.children.${index}.name`)} />
                          </div>
                          <div className="md:col-span-2 space-y-2">
                            <Label>Date of Birth</Label>
                            <Input type="date" disabled={readOnly} {...register(`family_background.children.${index}.dob`)} />
                          </div>
                          <div className="md:col-span-1 space-y-2">
                            <Label>Age</Label>
                            <Input type="number" disabled={readOnly} {...register(`family_background.children.${index}.age`)} />
                          </div>
                          {!readOnly && (
                            <div className="md:col-span-1">
                              <Button 
                                type="button" 
                                variant="ghost" 
                                size="icon" 
                                onClick={() => removeChild(index)}
                                className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                      {/* Mother */}
                      <div className="space-y-3 p-4 border rounded-lg">
                        <h4 className="font-medium text-gray-700">Mother's Details</h4>
                        <div className="space-y-2">
                          <Label>Name</Label>
                          <Input disabled={readOnly} {...register("family_background.mother.name")} />
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div className="space-y-2">
                            <Label>Date of Birth</Label>
                            <Input type="date" disabled={readOnly} {...register("family_background.mother.dob")} />
                          </div>
                          <div className="space-y-2">
                            <Label>Age</Label>
                            <Input type="number" disabled={readOnly} {...register("family_background.mother.age")} />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label>Occupation</Label>
                          <Input disabled={readOnly} {...register("family_background.mother.occupation")} />
                        </div>
                        <div className="flex items-center space-x-2 pt-2">
                          <Checkbox id="mother_deceased" disabled={readOnly} onCheckedChange={(checked) => document.getElementById('mother_deceased_hidden').click()} />
                          <input type="checkbox" id="mother_deceased_hidden" className="hidden" {...register("family_background.mother.is_deceased")} />
                          <Label htmlFor="mother_deceased">Deceased?</Label>
                        </div>
                      </div>

                      {/* Father */}
                      <div className="space-y-3 p-4 border rounded-lg">
                        <h4 className="font-medium text-gray-700">Father's Details</h4>
                        <div className="space-y-2">
                          <Label>Name</Label>
                          <Input disabled={readOnly} {...register("family_background.father.name")} />
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div className="space-y-2">
                            <Label>Date of Birth</Label>
                            <Input type="date" disabled={readOnly} {...register("family_background.father.dob")} />
                          </div>
                          <div className="space-y-2">
                            <Label>Age</Label>
                            <Input type="number" disabled={readOnly} {...register("family_background.father.age")} />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label>Occupation</Label>
                          <Input disabled={readOnly} {...register("family_background.father.occupation")} />
                        </div>
                        <div className="flex items-center space-x-2 pt-2">
                          <Checkbox id="father_deceased" disabled={readOnly} onCheckedChange={(checked) => document.getElementById('father_deceased_hidden').click()} />
                          <input type="checkbox" id="father_deceased_hidden" className="hidden" {...register("family_background.father.is_deceased")} />
                          <Label htmlFor="father_deceased">Deceased?</Label>
                        </div>
                      </div>
                    </div>
                  </div>

                  <Separator className="my-6" />

                  {/* Contact & Government IDs */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold text-gray-900">CONTACT INFORMATION</h3>
                      <div className="space-y-2">
                        <Label>Mobile Number <span className="text-red-500">*</span></Label>
                        <Input disabled={readOnly} {...register("contact_info.mobile_number", { required: true })} />
                      </div>
                      <div className="space-y-2">
                        <Label>Email Address <span className="text-red-500">*</span></Label>
                        <Input type="email" disabled={readOnly} {...register("contact_info.email_address", { required: true })} />
                      </div>
                      <div className="space-y-2">
                        <Label>Landline Number</Label>
                        <Input disabled={readOnly} {...register("contact_info.landline_number")} />
                      </div>
                      <div className="space-y-2">
                        <Label>Skype/Yahoo/etc.</Label>
                        <Input disabled={readOnly} {...register("contact_info.skype_yahoo")} />
                      </div>
                    </div>

                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold text-gray-900">GOVERNMENT IDs</h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2"><Label>SSS No.</Label><Input disabled={readOnly} {...register("government_ids.sss_no")} /></div>
                        <div className="space-y-2"><Label>TIN No.</Label><Input disabled={readOnly} {...register("government_ids.tin_no")} /></div>
                        <div className="space-y-2"><Label>PhilHealth</Label><Input disabled={readOnly} {...register("government_ids.philhealth_no")} /></div>
                        <div className="space-y-2"><Label>Pag-IBIG</Label><Input disabled={readOnly} {...register("government_ids.pagibig_no")} /></div>
                      </div>
                    </div>
                  </div>

                  <Separator className="my-6" />

                  {/* Emergency Contact */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900">CASE OF EMERGENCY</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label>Contact Person <span className="text-red-500">*</span></Label>
                        <Input disabled={readOnly} {...register("emergency_contact.name", { required: true })} />
                      </div>
                      <div className="space-y-2">
                        <Label>Relationship <span className="text-red-500">*</span></Label>
                        <Input disabled={readOnly} {...register("emergency_contact.relationship", { required: true })} />
                      </div>
                      <div className="space-y-2">
                        <Label>Contact Number <span className="text-red-500">*</span></Label>
                        <Input disabled={readOnly} {...register("emergency_contact.contact_number", { required: true })} />
                      </div>
                    </div>
                  </div>

                  {/* Next Button */}
                  <div className="pt-8 flex justify-end">
                    <Button 
                      type="button" 
                      onClick={handleNext}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-8"
                    >
                      Next Step <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}

              {/* STEP 2: Educational Background */}
              {step === 2 && (
                <div className="animate-in fade-in slide-in-from-right-8 duration-500 space-y-6">
                  
                  {/* Post Graduate */}
                  <div className="space-y-4">
                    <h4 className="font-medium text-gray-700">Post Graduate</h4>
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                      <div className="md:col-span-5 space-y-2">
                        <Label>School/Institution</Label>
                        <Input disabled={readOnly} {...register("educational_background.post_graduate.school")} />
                      </div>
                      <div className="md:col-span-3 space-y-2">
                        <Label>Course</Label>
                        <Input disabled={readOnly} {...register("educational_background.post_graduate.course")} />
                      </div>
                      <div className="md:col-span-2 space-y-2">
                        <Label>From</Label>
                        <Input disabled={readOnly} {...register("educational_background.post_graduate.from_date")} placeholder="Year" />
                      </div>
                      <div className="md:col-span-2 space-y-2">
                        <Label>To</Label>
                        <Input disabled={readOnly} {...register("educational_background.post_graduate.to_date")} placeholder="Year" />
                      </div>
                    </div>
                  </div>

                  {/* College */}
                  <div className="space-y-4">
                    <h4 className="font-medium text-gray-700">College</h4>
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                      <div className="md:col-span-5 space-y-2">
                        <Label>School/Institution</Label>
                        <Input disabled={readOnly} {...register("educational_background.college.school")} />
                      </div>
                      <div className="md:col-span-3 space-y-2">
                        <Label>Course</Label>
                        <Input disabled={readOnly} {...register("educational_background.college.course")} />
                      </div>
                      <div className="md:col-span-2 space-y-2">
                        <Label>From</Label>
                        <Input disabled={readOnly} {...register("educational_background.college.from_date")} placeholder="Year" />
                      </div>
                      <div className="md:col-span-2 space-y-2">
                        <Label>To</Label>
                        <Input disabled={readOnly} {...register("educational_background.college.to_date")} placeholder="Year" />
                      </div>
                    </div>
                  </div>

                  {/* Secondary */}
                  <div className="space-y-4">
                    <h4 className="font-medium text-gray-700">Secondary</h4>
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                      <div className="md:col-span-5 space-y-2">
                        <Label>School</Label>
                        <Input disabled={readOnly} {...register("educational_background.secondary.school")} />
                      </div>
                      <div className="md:col-span-3 space-y-2">
                        <Label>Address</Label>
                        <Input disabled={readOnly} {...register("educational_background.secondary.address")} />
                      </div>
                      <div className="md:col-span-2 space-y-2">
                        <Label>From</Label>
                        <Input disabled={readOnly} {...register("educational_background.secondary.from_date")} placeholder="Year" />
                      </div>
                      <div className="md:col-span-2 space-y-2">
                        <Label>To</Label>
                        <Input disabled={readOnly} {...register("educational_background.secondary.to_date")} placeholder="Year" />
                      </div>
                    </div>
                  </div>

                  {/* Primary */}
                  <div className="space-y-4">
                    <h4 className="font-medium text-gray-700">Primary</h4>
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                      <div className="md:col-span-5 space-y-2">
                        <Label>School</Label>
                        <Input disabled={readOnly} {...register("educational_background.primary.school")} />
                      </div>
                      <div className="md:col-span-3 space-y-2">
                        <Label>Address</Label>
                        <Input disabled={readOnly} {...register("educational_background.primary.address")} />
                      </div>
                      <div className="md:col-span-2 space-y-2">
                        <Label>From</Label>
                        <Input disabled={readOnly} {...register("educational_background.primary.from_date")} placeholder="Year" />
                      </div>
                      <div className="md:col-span-2 space-y-2">
                        <Label>To</Label>
                        <Input disabled={readOnly} {...register("educational_background.primary.to_date")} placeholder="Year" />
                      </div>
                    </div>
                  </div>

                  <Separator className="my-6" />

                  {/* Honors / Awards / Exams */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label>Honors and Awards Received</Label>
                      <Textarea disabled={readOnly} {...register("educational_background.honors_awards")} className="min-h-[100px]" />
                    </div>
                    <div className="space-y-2">
                      <Label>Extra Curricular Activities / Organizational Membership</Label>
                      <Textarea disabled={readOnly} {...register("educational_background.extra_curricular")} className="min-h-[100px]" />
                    </div>
                    <div className="md:col-span-2 space-y-2">
                      <Label>Government Examination/s Taken</Label>
                      <Input disabled={readOnly} {...register("educational_background.government_exams")} />
                    </div>
                  </div>

                  {/* Seminars / Trainings */}
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <Label className="font-medium">Seminars / Trainings Attended</Label>
                      {!readOnly && (
                        <Button 
                          type="button" 
                          variant="outline" 
                          size="sm" 
                          onClick={() => appendSeminar({ topic: '', venue: '', date: '' })}
                        >
                          <Plus className="h-4 w-4 mr-1" /> Add Seminar
                        </Button>
                      )}
                    </div>
                    
                    {seminarFields.map((field, index) => (
                      <div key={field.id} className="grid grid-cols-1 md:grid-cols-7 gap-4 items-end bg-gray-50 p-3 rounded-lg">
                        <div className="md:col-span-3 space-y-2">
                          <Label>Topic</Label>
                          <Input disabled={readOnly} {...register(`educational_background.seminars_trainings.${index}.topic`)} />
                        </div>
                        <div className="md:col-span-2 space-y-2">
                          <Label>Venue/Location</Label>
                          <Input disabled={readOnly} {...register(`educational_background.seminars_trainings.${index}.venue`)} />
                        </div>
                        <div className="md:col-span-1 space-y-2">
                          <Label>Date/Year</Label>
                          <Input disabled={readOnly} {...register(`educational_background.seminars_trainings.${index}.date`)} />
                        </div>
                        {!readOnly && (
                          <div className="md:col-span-1">
                            <Button 
                              type="button" 
                              variant="ghost" 
                              size="icon" 
                              onClick={() => removeSeminar(index)}
                              className="text-red-500 hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  <Separator className="my-6" />

                  {/* Employment History */}
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <Label className="font-medium text-lg">EMPLOYMENT HISTORY</Label>
                      {!readOnly && (
                        <Button 
                          type="button" 
                          variant="outline" 
                          size="sm" 
                          onClick={() => appendEmployment({ company: '', position: '', from_date: '', to_date: '', salary: '', address: '', superior: '', reason: '' })}
                        >
                          <Plus className="h-4 w-4 mr-1" /> Add Employment
                        </Button>
                      )}
                    </div>

                    {employmentFields.map((field, index) => (
                      <div key={field.id} className="grid grid-cols-1 gap-4 bg-gray-50 p-4 rounded-lg border">
                        {!readOnly && (
                          <div className="flex justify-end">
                             <Button 
                              type="button" 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => removeEmployment(index)}
                              className="text-red-500 hover:bg-red-50 h-6 px-2"
                            >
                              <Trash2 className="h-4 w-4" /> Remove
                            </Button>
                          </div>
                        )}
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                          <div className="md:col-span-1 space-y-2">
                            <Label>Company</Label>
                            <Input disabled={readOnly} {...register(`employment_history.${index}.company`)} />
                          </div>
                          <div className="md:col-span-1 space-y-2">
                            <Label>Position Held</Label>
                            <Input disabled={readOnly} {...register(`employment_history.${index}.position`)} />
                          </div>
                          <div className="md:col-span-2 grid grid-cols-3 gap-2">
                             <div className="space-y-2">
                                <Label>From</Label>
                                <Input disabled={readOnly} {...register(`employment_history.${index}.from_date`)} placeholder="Date/Year" />
                             </div>
                             <div className="space-y-2">
                                <Label>To</Label>
                                <Input disabled={readOnly} {...register(`employment_history.${index}.to_date`)} placeholder="Date/Year" />
                             </div>
                             <div className="space-y-2">
                                <Label>Last Salary</Label>
                                <Input disabled={readOnly} {...register(`employment_history.${index}.salary`)} />
                             </div>
                          </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label>Address / Contact Number</Label>
                            <Input disabled={readOnly} {...register(`employment_history.${index}.address`)} />
                          </div>
                          <div className="space-y-2">
                            <Label>Name and Position of Superior</Label>
                            <Input disabled={readOnly} {...register(`employment_history.${index}.superior`)} />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label>Reason/s for leaving</Label>
                          <Textarea disabled={readOnly} {...register(`employment_history.${index}.reason`)} className="min-h-[60px]" />
                        </div>
                      </div>
                    ))}
                    {employmentFields.length === 0 && <p className="text-sm text-gray-500 italic">No employment history added.</p>}
                  </div>

                  <Separator className="my-6" />

                  {/* Referral Info */}
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>Where did you come to know of the openings at iSource?</Label>
                      <Input disabled={readOnly} {...register("referral_info.source")} className="border-b-2 border-t-0 border-x-0 rounded-none focus-visible:ring-0 px-0" placeholder="e.g. Facebook, JobStreet, Referral" />
                    </div>

                    <div className="space-y-4 pt-4">
                      <div className="flex items-center gap-4">
                        <Label>Do you know have relatives/friends who have worked and/or are working in iSource?</Label>
                        <div className="flex items-center gap-4">
                          <div className="flex items-center space-x-2">
                            <input 
                              type="radio" 
                              id="relatives_yes" 
                              value="true"
                              className="accent-blue-600 h-4 w-4"
                              disabled={readOnly}
                              {...register("referral_info.has_relatives")} 
                            />
                            <Label htmlFor="relatives_yes">Yes</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <input 
                              type="radio" 
                              id="relatives_no" 
                              value="false"
                              className="accent-blue-600 h-4 w-4"
                              disabled={readOnly}
                              {...register("referral_info.has_relatives")} 
                            />
                            <Label htmlFor="relatives_no">No</Label>
                          </div>
                        </div>
                      </div>
                      
                      <div className="space-y-2 pl-4 border-l-2 border-gray-200">
                        <div className="flex justify-between items-center">
                           <Label className="text-sm text-gray-600">If yes, please state the name and position:</Label>
                           {!readOnly && (
                             <Button 
                              type="button" 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => appendRelative({ name: '', position: '', relationship: '' })}
                              className="text-blue-600 h-6"
                            >
                              <Plus className="h-3 w-3 mr-1" /> Add Person
                            </Button>
                           )}
                        </div>
                        
                        {relativeFields.map((field, index) => (
                           <div key={field.id} className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
                              <div className="md:col-span-4 space-y-1">
                                <Label className="text-xs">Name</Label>
                                <Input disabled={readOnly} {...register(`referral_info.relatives.${index}.name`)} className="h-8" />
                              </div>
                              <div className="md:col-span-4 space-y-1">
                                <Label className="text-xs">Position</Label>
                                <Input disabled={readOnly} {...register(`referral_info.relatives.${index}.position`)} className="h-8" />
                              </div>
                              <div className="md:col-span-3 space-y-1">
                                <Label className="text-xs">Relationship</Label>
                                <div className="flex gap-4 pt-2">
                                   <div className="flex items-center space-x-1">
                                      <input type="checkbox" disabled={readOnly} {...register(`referral_info.relatives.${index}.is_relative`)} className="h-4 w-4" />
                                      <span className="text-sm">Relative</span>
                                   </div>
                                   <div className="flex items-center space-x-1">
                                      <input type="checkbox" disabled={readOnly} {...register(`referral_info.relatives.${index}.is_friend`)} className="h-4 w-4" />
                                      <span className="text-sm">Friend</span>
                                   </div>
                                </div>
                              </div>
                              {!readOnly && (
                                <div className="md:col-span-1">
                                  <Button 
                                    type="button" 
                                    variant="ghost" 
                                    size="icon" 
                                    onClick={() => removeRelative(index)}
                                    className="text-red-500 h-8 w-8"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              )}
                           </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="pt-8 flex justify-between items-center">
                    <Button 
                      type="button" 
                      variant="outline"
                      onClick={() => setStep(1)}
                    >
                      Back
                    </Button>
                    <Button 
                      type="button" 
                      onClick={handleNext}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-8"
                    >
                      Next Step <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}

              {/* STEP 3: Medical Background */}
              {step === 3 && (
                <div className="animate-in fade-in slide-in-from-right-8 duration-500 space-y-6">
                  
                  <div className="bg-yellow-50 p-4 rounded-md border border-yellow-200 text-sm text-yellow-800">
                    <p>The following medical questions must be fully answered/accomplished. Any condition or disease which occurred on or before the date of submission of this Application Form must be stated herein.</p>
                    <p className="mt-2 font-medium">Any omission, misrepresentation or failure to provide medical information is a ground for immediate termination of your employment.</p>
                  </div>

                  <div className="space-y-4">
                    <h3 className="font-semibold text-gray-900 border-b pb-2">FOR FEMALE APPLICANTS</h3>
                    <div className="flex flex-wrap gap-6 items-center">
                      <div className="flex items-center gap-4">
                        <Label>Are you NOW pregnant?</Label>
                        <div className="flex items-center space-x-2">
                          <input type="radio" id="pregnant_yes" value="true" className="accent-blue-600 h-4 w-4" disabled={readOnly} {...register("medical_background.is_pregnant")} />
                          <Label htmlFor="pregnant_yes">Yes</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <input type="radio" id="pregnant_no" value="false" className="accent-blue-600 h-4 w-4" disabled={readOnly} {...register("medical_background.is_pregnant")} />
                          <Label htmlFor="pregnant_no">No</Label>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Label>If yes, expected date of delivery:</Label>
                        <Input type="date" disabled={readOnly} {...register("medical_background.delivery_date")} className="w-auto" />
                      </div>
                    </div>
                  </div>

                  <Separator className="my-6" />

                  <div className="space-y-4">
                    <h3 className="font-semibold text-gray-900 border-b pb-2">Have you had or still have the following?</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2">
                      {[
                        "Asthma, difficulty of breathing, chronic bronchitis, emphysema",
                        "Chronic cough, spitting of blood, tuberculosis, primary complex",
                        "Abdominal pain, hyperacidity, peptic ulcer disease",
                        "Chronic diarrhea, blood-streaked stools, hemorrhoids, fistula",
                        "Gall bladder diseases/stones",
                        "Edema",
                        "Hernia (all types)",
                        "Liver condition (e.g. cirrhosis, jaundice, hepatitis)",
                        "Chest pain, palpitations, heart murmurs, rheumatic fever",
                        "Hypertension",
                        "Kidney or urinary tract stone",
                        "Difficult and painful urination, loss of urine control, recurrent urinary infection",
                        "Prostate disorder (e.g. benign prostatic hypertrophy)",
                        "Female organ abnormality (e.g. endometriosis, ovarian cyst, irregular menses)",
                        "Sexually transmitted diseases (e.g. syphilis)",
                        "Serious eye condition (e.g. cataract, glaucoma)",
                        "Loss of hearing, perforated eardrum, chronic ear discharge/problem",
                        "Sinusitis, hay fever, allergies",
                        "Persistent hoarseness, stridor, recurrent nose bleeding, nasal polyps",
                        "Convulsion, seizure or epilepsy",
                        "Recurrent/severe headache, dizziness, vertigo, loss of consciousness",
                        "Numbness/pain/swelling of extremities, varicose vein, peripheral vascular diseases",
                        "Anemia and/or other blood disease",
                        "Back injury or persistent back pain (e.g. scoliosis or lumbago)",
                        "Any bodily injury or disability",
                        "Arthritis or painful joints",
                        "Diabetes",
                        "Goiter or thyroid conditions",
                        "Serious skin disease, other chronic dermatoses (e.g. psoriasis, leprosy)",
                        "Tumor or cyst (e.g. breast mass)",
                        "Alcoholism or drug dependency",
                        "Mental or emotional disorders: psycho social dependency",
                        "Congenital/hereditary disorder"
                      ].map((condition) => (
                        <div key={condition} className="flex items-start space-x-2 py-1">
                          <input 
                            type="checkbox" 
                            value={condition} 
                            disabled={readOnly}
                            {...register("medical_background.conditions")} 
                            className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <Label className="text-sm font-normal leading-tight">{condition}</Label>
                        </div>
                      ))}
                      <div className="flex items-center space-x-2 py-1 md:col-span-2">
                        <Label className="w-auto whitespace-nowrap">Others, please specify:</Label>
                        <Input disabled={readOnly} {...register("medical_background.others")} className="h-8" />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4 pt-4">
                    <div className="space-y-2">
                      <Label>If you answered YES to any of the above, please explain further (Use separate sheet if needed)</Label>
                      <Textarea disabled={readOnly} {...register("medical_background.conditions_explanation")} />
                    </div>
                    <div className="space-y-2">
                      <Label>Please list down medicines you have taken in the past or are presently taking for the treatment of the particular medical condition.</Label>
                      <Textarea disabled={readOnly} {...register("medical_background.medications")} />
                    </div>
                    <div className="space-y-2">
                      <Label>Have you ever consulted and/or been hospitalized of any medical condition or its complications? If yes, please provide details.</Label>
                      <Textarea disabled={readOnly} {...register("medical_background.hospitalization_history")} />
                    </div>
                    <div className="space-y-2">
                      <Label>Have you even been advised to undergo further treatment or surgery which you have not yet undergone?</Label>
                      <Textarea disabled={readOnly} {...register("medical_background.advised_treatment")} />
                    </div>
                    <div className="space-y-2">
                      <Label>Have you undergone any surgical procedure?</Label>
                      <Textarea disabled={readOnly} {...register("medical_background.surgical_procedure")} />
                    </div>
                    <div className="space-y-2">
                      <Label>Have you visited a physician in the last year?</Label>
                      <Textarea disabled={readOnly} {...register("medical_background.physician_visit")} />
                    </div>
                  </div>

                  <div className="pt-8 flex justify-between items-center">
                    <Button 
                      type="button" 
                      variant="outline"
                      onClick={() => setStep(2)}
                    >
                      Back
                    </Button>
                    <Button 
                      type="button" 
                      onClick={handleNext}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-8"
                    >
                      Next Step <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}

              {/* STEP 4: References & Declaration */}
              {step === 4 && (
                <div className="animate-in fade-in slide-in-from-right-8 duration-500 space-y-6">
                  
                  {/* Character References */}
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <Label className="font-medium text-lg">CHARACTER REFERENCES</Label>
                      {!readOnly && (
                        <Button 
                          type="button" 
                          variant="outline" 
                          size="sm" 
                          onClick={() => appendReference({ name: '', company: '', position: '', contact_number: '' })}
                        >
                          <Plus className="h-4 w-4 mr-1" /> Add Reference
                        </Button>
                      )}
                    </div>
                    <p className="text-sm text-gray-500">Not related to you by blood or marriage.</p>

                    {referenceFields.map((field, index) => (
                      <div key={field.id} className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end bg-gray-50 p-3 rounded-lg">
                        <div className="md:col-span-3 space-y-2">
                          <Label>Name</Label>
                          <Input disabled={readOnly} {...register(`character_references.${index}.name`)} />
                        </div>
                        <div className="md:col-span-3 space-y-2">
                          <Label>Company</Label>
                          <Input disabled={readOnly} {...register(`character_references.${index}.company`)} />
                        </div>
                        <div className="md:col-span-3 space-y-2">
                          <Label>Position</Label>
                          <Input disabled={readOnly} {...register(`character_references.${index}.position`)} />
                        </div>
                        <div className="md:col-span-2 space-y-2">
                          <Label>Contact Number</Label>
                          <Input disabled={readOnly} {...register(`character_references.${index}.contact_number`)} />
                        </div>
                        {!readOnly && (
                          <div className="md:col-span-1">
                            <Button 
                              type="button" 
                              variant="ghost" 
                              size="icon" 
                              onClick={() => removeReference(index)}
                              className="text-red-500 hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  <Separator className="my-6" />

                  {/* Declaration */}
                  <div className="space-y-6">
                    <h3 className="text-lg font-semibold text-gray-900">DECLARATION</h3>
                    <div className="bg-gray-50 p-6 rounded-lg border text-justify text-sm leading-relaxed text-gray-700">
                      <p>
                        I hereby certify that the information provided in this application form is true and correct to the best of my knowledge and belief. 
                        I understand that any misrepresentation or omission of facts shall be considered sufficient cause for the cancellation of my application 
                        or separation from the company if I have already been employed.
                      </p>
                      <p className="mt-4">
                        I hereby authorize the company to verify all information provided herein and to conduct a background check on my character and employment history.
                      </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 pt-4">
                      <div className="space-y-2">
                        <Label>Signature (Printed Name)</Label>
                        <Input 
                          disabled={readOnly}
                          {...register("declaration.signature_name", { required: true })} 
                          placeholder="Type your full name as signature"
                          className="border-b-2 border-t-0 border-x-0 rounded-none focus-visible:ring-0 px-0"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Date</Label>
                        <Input 
                          type="date" 
                          disabled={readOnly}
                          {...register("declaration.date", { required: true })} 
                          className="border-b-2 border-t-0 border-x-0 rounded-none focus-visible:ring-0 px-0"
                        />
                      </div>
                    </div>
                  </div>

                  <Separator className="my-6" />

                  {/* Resume Upload (Moved to Step 3) */}
                  <div className="space-y-4">
                    <Label className="font-medium">Resume / CV / Supporting Documents</Label>
                    <div className="flex flex-col gap-2">
                      <div className="flex items-center gap-4">
                        {!readOnly ? (
                          <>
                            <div className="relative">
                              <Input 
                                type="file" 
                                onChange={handleFileUpload}
                                className="cursor-pointer max-w-sm"
                                disabled={isUploading}
                                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                              />
                            </div>
                            {isUploading && (
                              <div className="flex items-center text-blue-600">
                                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                <span className="text-sm">Uploading...</span>
                              </div>
                            )}
                          </>
                        ) : (
                          getValues("resume_file") ? (
                            <a 
                              href={getValues("resume_file")} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="flex items-center text-blue-600 hover:text-blue-800 hover:underline"
                            >
                              <FileText className="h-4 w-4 mr-2" />
                              View Uploaded Resume
                            </a>
                          ) : (
                            <span className="text-gray-500 italic text-sm">No resume uploaded.</span>
                          )
                        )}
                      </div>
                      <input type="hidden" {...register("resume_file")} />
                      {!readOnly && getValues("resume_file") && !isUploading && (
                        <p className="text-sm text-green-600 flex items-center">
                          <Upload className="h-4 w-4 mr-1" /> File uploaded ready for submission
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Final Submit Button */}
                  <div className="pt-8 flex justify-between items-center">
                    <Button 
                      type="button" 
                      variant="outline"
                      onClick={() => setStep(3)}
                    >
                      Back
                    </Button>
                    {!readOnly && (
                      <Button 
                        type="submit" 
                        className="bg-green-600 hover:bg-green-700 text-white px-8"
                        disabled={isSubmitting || isUploading}
                      >
                        {isSubmitting ? (
                          <>
                            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                            Submitting...
                          </>
                        ) : (
                          "Submit Application"
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              )}

            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ApplicationForm;
